import { styled } from "@mui/material";
import { ChevronRight, Cube, Eye, EyeOff } from "@procore/core-icons";
import { Spinner } from "@procore/core-react";
import { useContext, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";

import { useExplorerGlobals } from "contexts/explorerGlobals";
import { useDispatchHidden, useHidden } from "contexts/hidden";
import { TreeNodeRendererProps } from "features/modelTree/types";
import {
    hideObjectHierarchy,
    hideObjectHierarchyFromFilteredTree,
    showObjectHierarchy,
    showObjectHierarchyFromFilteredTree,
} from "utils/objectActions";
import { getParentPaths, isContainerFileNode } from "utils/objectPath";
import { highlightText } from "utils/textHighlight";
import { getGroupStateForFilteredTree, getGroupStateForIdSet } from "utils/treeHelpers";

import { CLICK_DELAY } from "../constants";
import { invertGroupState, useIsolationVisibility } from "../hooks/useIsolationVisibility";
import { SelectionContext } from "../selectionContext";
import { DelayedTooltip } from "./DelayedTooltip";
import { useDelayedTooltip } from "./useDelayedTooltip";

const Row = styled("div", { shouldForwardProp: (p) => p !== "isSelected" && p !== "isSticky" })<{
    isSelected: boolean;
    isSticky?: boolean;
}>(({ isSelected, isSticky }) => ({
    display: "flex",
    alignItems: "center",
    flexGrow: 1,
    minWidth: 0,
    overflow: "hidden",
    marginLeft: 8,
    marginRight: 8,
    height: 30,
    paddingLeft: 8,
    paddingRight: 8,
    gap: 4,
    cursor: "default",
    userSelect: "none",
    boxSizing: "border-box",
    backgroundColor: isSelected && !isSticky ? "#d2e0f9" : "#ffffff",
    ...(!isSticky && {
        "&:hover": { backgroundColor: isSelected ? "#d2e0f9" : "#f4f5f6" },
        "&:hover .ot-vis-btn": { visibility: "visible", pointerEvents: "auto" },
        "&:hover .ot-hint-dot": { display: "none" },
    }),
    "& .ot-vis-btn": { visibility: "hidden", pointerEvents: "none" },
    "& .ot-vis-btn:hover": { background: "rgba(0,0,0,0.06)" },
    ...(isSticky && {
        pointerEvents: "none",
        "& *": { pointerEvents: "none" },
    }),
}));

type ConnectorType = "through" | "branch" | "corner" | "empty";

function Connector({ type, inSelection }: { type: ConnectorType; inSelection: boolean }) {
    const lineColor = inSelection ? "#bcd1f5" : "#d6dadc";
    return (
        <div style={{ position: "relative", flexShrink: 0, height: "100%", overflow: "hidden", width: 14 }}>
            {type === "through" && (
                <div
                    style={{
                        position: "absolute",
                        top: 0,
                        bottom: 0,
                        left: 8,
                        width: 1,
                        backgroundColor: lineColor,
                    }}
                />
            )}
            {(type === "branch" || type === "corner") && (
                <>
                    <div
                        style={{
                            position: "absolute",
                            top: 0,
                            bottom: type === "corner" ? 15 : 0,
                            left: 8,
                            width: 1,
                            backgroundColor: lineColor,
                        }}
                    />
                    <div
                        style={{
                            position: "absolute",
                            left: 9,
                            top: "50%",
                            width: 5,
                            height: 1,
                            transform: "translateY(-50%)",
                            backgroundColor: lineColor,
                        }}
                    />
                </>
            )}
        </div>
    );
}

export function ObjectTreeNodeProcoreV2({
    item,
    onToggleExpanded,
    onSelectInTree,
    tree,
    filteredObjectIds,
    searchTerm,
    onNodeRightClick,
    abortSignal,
    setLoading,
    isSticky,
}: TreeNodeRendererProps) {
    const { node, level } = item;
    const sel = useContext(SelectionContext);
    const path = node.path;
    const isSelected = sel?.selectedRowPaths.has(path) ?? false;
    const idx = sel ? sel.list.findIndex((i) => i.node.path === path) : -1;
    const isPrevRowSelected = idx > 0 && (sel?.selectedRowPaths.has(sel.list[idx - 1].node.path) ?? false);
    const isNextRowSelected =
        idx < (sel?.list.length ?? 0) - 1 && (sel?.selectedRowPaths.has(sel.list[idx + 1].node.path) ?? false);
    const onRowSelect = sel?.onRowSelect;
    const onToggleVisibilityForSelection = sel?.onToggleVisibilityForSelection;
    const { t } = useTranslation();
    const singleClickTimerRef = useRef<number | null>(null);

    const {
        state: { db },
    } = useExplorerGlobals(true);
    const dispatchHidden = useDispatchHidden();
    const hidden = useHidden();
    const hiddenIds = hidden.ids;
    const isolation = useIsolationVisibility();
    const isolationVisibleIds = isolation.visibleIds;

    const hiddenState = useMemo(() => {
        const getState = (ids: typeof hiddenIds) =>
            filteredObjectIds != null
                ? getGroupStateForFilteredTree(ids, item, filteredObjectIds, tree)
                : getGroupStateForIdSet(ids, item, tree);
        if (isolationVisibleIds) {
            return invertGroupState(getState(isolationVisibleIds));
        }
        return getState(hiddenIds);
    }, [hiddenIds, isolationVisibleIds, item, tree, filteredObjectIds]);
    const isHidden = useMemo(() => {
        if (hiddenState === "all") {
            return true;
        }
        if (isolationVisibleIds) {
            return false;
        }
        if (node.isLeaf) {
            return false;
        }
        return hiddenIds.has(node.data.object.id);
    }, [node, hiddenState, hiddenIds, isolationVisibleIds]);
    const hasPartiallyHiddenChildren = !isHidden && hiddenState === "some";

    const labelTooltip = useDelayedTooltip<HTMLSpanElement>({
        getText: (el) => (el.scrollWidth > el.offsetWidth ? node.name : null),
    });
    const hideTooltip = useDelayedTooltip<HTMLButtonElement>({
        getText: () => (isHidden ? t("showObjects") : t("hideObjects")),
    });

    // Build connector columns (one per ancestor level) from the node path + tree dict.
    // ancestorPaths[0] = root (""), [1] = level-0 ancestor, ..., [level] = direct parent.
    const connectors = useMemo((): ConnectorType[] => {
        if (level === 0) {
            return [];
        }
        const ancestorPaths = [...getParentPaths(node.path)];
        const result: ConnectorType[] = [];
        // Start at i=2 to skip the level-0 ancestor (file nodes have no connector column).
        // Column for a level-k ancestor (k>=1) lands at index k-1, matching that ancestor's
        // own connector position in its own row.
        for (let i = 2; i <= level; i++) {
            const ancestorPath = ancestorPaths[i];
            const parentPath = ancestorPaths[i - 1];
            const parent = tree[parentPath];
            const siblings = parent?.children ?? [];
            const isLastSibling = siblings.length > 0 && siblings[siblings.length - 1] === ancestorPath;
            result.push(isLastSibling ? "empty" : "through");
        }
        result.push(item.isLast ? "corner" : "branch");
        return result;
    }, [level, node.path, item.isLast, tree]);

    // Hint dot: collapsed folder with some-but-not-all children hidden.
    const showHintDot = hasPartiallyHiddenChildren && !node.isLeaf && !node.expanded;

    // Contiguous border-radius: round only the outer corners of a selected run (req 13)
    const topRadius = isSelected && !isPrevRowSelected ? 3 : 0;
    const bottomRadius = isSelected && !isNextRowSelected ? 3 : 0;
    const borderRadius =
        isSelected && !isSticky ? `${topRadius}px ${topRadius}px ${bottomRadius}px ${bottomRadius}px` : 3;

    // Ticket req 8: hidden rows use grey text only (no grey background)
    const textColor = isHidden ? "#9aa5b1" : isSelected && !isSticky ? "#0a1f43" : "#232729";

    const handleRowClick = (e: React.MouseEvent) => {
        // Stop bubbling so the empty-space deselect-all handler in the window wrapper
        // only fires when clicking below all rows, not when clicking a row.
        e.stopPropagation();
        if (singleClickTimerRef.current !== null) {
            window.clearTimeout(singleClickTimerRef.current);
            singleClickTimerRef.current = null;
        }
        singleClickTimerRef.current = window.setTimeout(() => {
            singleClickTimerRef.current = null;
            // Notify ModelTree that this is a tree-initiated selection (prevents auto-scroll)
            onSelectInTree?.(node.data.object.id);
            onRowSelect?.(node.path, e);
        }, CLICK_DELAY);
    };

    const handleRowDoubleClick = () => {
        if (singleClickTimerRef.current !== null) {
            window.clearTimeout(singleClickTimerRef.current);
            singleClickTimerRef.current = null;
        }
        if (!node.isLeaf) {
            onToggleExpanded(item);
        }
    };

    const handleToggleVisibility = async (e: React.MouseEvent) => {
        e.stopPropagation();
        if (isSelected && onToggleVisibilityForSelection) {
            onToggleVisibilityForSelection(isHidden);
            return;
        }
        if (isolationVisibleIds) {
            await isolation.setHierarchyVisible({
                nodes: [node.data.object],
                visible: isHidden,
                db,
                abortSignal,
                setLoading,
                filteredObjectIds,
            });
            return;
        }
        if (isHidden) {
            const params = { nodes: [node.data.object], db, abortSignal, setLoading, dispatchHidden };
            if (filteredObjectIds != null) {
                await showObjectHierarchyFromFilteredTree({ ...params, filteredObjectIds });
            } else {
                await showObjectHierarchy(params);
            }
        } else {
            const params = { nodes: [node.data.object], db, abortSignal, setLoading, dispatchHidden };
            if (filteredObjectIds != null) {
                await hideObjectHierarchyFromFilteredTree({ ...params, filteredObjectIds });
            } else {
                await hideObjectHierarchy(params);
            }
        }
    };

    return (
        <Row
            isSelected={isSelected}
            isSticky={isSticky}
            style={{ borderRadius }}
            onClick={isSticky ? undefined : handleRowClick}
            onDoubleClick={isSticky ? undefined : handleRowDoubleClick}
            onContextMenu={
                isSticky
                    ? undefined
                    : (e) => {
                          e.preventDefault();
                          onNodeRightClick?.(e.clientX, e.clientY, item);
                      }
            }
        >
            {connectors.map((type, i) => (
                <Connector key={i} type={type} inSelection={isSelected && !isSticky} />
            ))}

            {node.isLeaf || isSticky ? (
                <div style={{ flexShrink: 0, width: 16, height: 16 }} />
            ) : (
                <button
                    type="button"
                    data-testid="v2-chevron-button"
                    disabled={node.loading}
                    style={{
                        flexShrink: 0,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        cursor: node.loading ? "default" : "pointer",
                        padding: 0,
                        border: "none",
                        background: "transparent",
                        width: 16,
                        height: 16,
                    }}
                    onClick={(e) => {
                        e.stopPropagation();
                        onToggleExpanded(item);
                    }}
                    tabIndex={-1}
                >
                    {node.loading ? (
                        <Spinner size="xs" />
                    ) : (
                        <ChevronRight
                            style={{
                                width: 12,
                                height: 12,
                                color: textColor,
                                transform: node.expanded ? "rotate(90deg)" : undefined,
                                transition: "transform 0.15s ease",
                            }}
                        />
                    )}
                </button>
            )}

            {isContainerFileNode(node.path) && (
                <Cube
                    style={{
                        flexShrink: 0,
                        width: 16,
                        height: 16,
                        color: textColor,
                        opacity: isSelected && !isSticky ? 1 : 0.85,
                    }}
                />
            )}

            <span
                ref={labelTooltip.ref}
                style={{
                    flex: 1,
                    minWidth: 0,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    fontWeight: "normal",
                    color: textColor,
                    fontSize: 12,
                    lineHeight: "16px",
                    letterSpacing: "0.25px",
                    fontFamily: "Inter, sans-serif",
                }}
                onMouseEnter={labelTooltip.onMouseEnter}
                onMouseLeave={labelTooltip.onMouseLeave}
            >
                {searchTerm ? highlightText(node.name, searchTerm) : node.name}
            </span>

            <div style={{ position: "relative", flexShrink: 0, width: 24, height: 24 }}>
                {showHintDot && (
                    <div
                        className="ot-hint-dot"
                        style={{
                            position: "absolute",
                            inset: 0,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                        }}
                    >
                        <div style={{ width: 6, height: 6, borderRadius: "50%", backgroundColor: "#D6DADC" }} />
                    </div>
                )}
                <button
                    ref={hideTooltip.ref}
                    type="button"
                    className="ot-vis-btn"
                    style={{
                        position: "absolute",
                        inset: 0,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        cursor: "pointer",
                        padding: 4,
                        border: "none",
                        background: "transparent",
                        borderRadius: 4,
                        color: "#232729",
                        boxSizing: "border-box",
                    }}
                    onMouseEnter={hideTooltip.onMouseEnter}
                    onMouseLeave={hideTooltip.onMouseLeave}
                    onClick={(e) => void handleToggleVisibility(e)}
                    tabIndex={-1}
                >
                    {isHidden ? (
                        <EyeOff style={{ width: 16, height: 16 }} />
                    ) : (
                        <Eye style={{ width: 16, height: 16 }} />
                    )}
                </button>
            </div>
            <DelayedTooltip pos={labelTooltip.pos} maxWidth={400} />
            <DelayedTooltip pos={hideTooltip.pos} />
        </Row>
    );
}
